import { z } from "zod";
import { draftedTestDatasetSchema } from "./analyticsDraftedTest";
import { CollabPhaseSchema, collabPhaseDisplay } from "./collabPhase";
import { CollabMessageSchema } from "./collabMessage";

/**
 * Collaborative Work-Step Session Types
 *
 * Fork of collabControlSession.ts for the work-step collaborative AI generation
 * flow (paths `/work-steps/collab/...`). Work-step plan-domain deltas vs
 * controls (per Stage 5 constraints): NO control_type, NO key reports, NO
 * workpaper examples. A testing table is "sample" or "direct", as on controls.
 *
 * Wire casing deliberately differs from collabControlSession.ts: the
 * controls collab router converts its `Model`-based domain objects
 * (ControlDraftPlan, CollabGenerationSession, ...) into plain, unaliased
 * snake_case response DTOs before they leave the process — hence that file's
 * schemas use snake_case keys. The work-step backend types
 * (WorkStepDraftPlan/WorkStepPlanPatch/WorkStepGenerationSession, in
 * midship/models/heretic/work_step_collab_generation.py) extend `Model`
 * directly (`alias_generator=to_camel`, `populate_by_name=True`), and per the
 * Stage 5 contract the work-steps collab router serializes responses
 * camelCase on the wire. Request bodies stay snake_case (mirroring the
 * controls router's plain request DTOs) — `populate_by_name=True` accepts
 * either casing server-side, but snake_case is the established convention for
 * request payloads.
 */

// Phases of the collaborative session — identical to controls, so the
// vocabulary lives in collabPhase.ts. Re-exported here because this file is the
// work-step flow's single import surface.
export { CollabPhaseSchema, PHASE_ORDER } from "./collabPhase";
export type { CollabPhase } from "./collabPhase";

// Proposed attribute in the draft plan.
// `id` ties a draft item back to its persisted row: finalize matches by id, so
// dropping it turns an edit into delete-and-recreate (losing test history).
// zod strips undeclared keys, which is why it must be declared here.
export const WorkStepProposedAttributeSchema = z.object({
  id: z.string().nullable().optional(),
  reference: z.string(),
  statement: z.string(),
});
export type WorkStepProposedAttribute = z.infer<
  typeof WorkStepProposedAttributeSchema
>;

export const WorkStepDraftSupportColumnSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string(),
  description: z.string(),
  dataType: z.enum(["string", "number"]),
});
export type WorkStepDraftSupportColumn = z.infer<
  typeof WorkStepDraftSupportColumnSchema
>;

export const WorkStepDraftAttributeTestColumnSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable(),
  passCriteria: z.string(),
  failCriteria: z.string().nullable().optional(),
  naCriteria: z.string().nullable().optional(),
  procedureGuidance: z.string().nullable().optional(),
  tolerances: z.string().nullable().optional(),
  expectedEvidences: z.string().nullable().optional(),
  attributeReference: z.string().nullable().optional(),
});
export type WorkStepDraftAttributeTestColumn = z.infer<
  typeof WorkStepDraftAttributeTestColumnSchema
>;

export const WorkStepDraftAnalyticsColumnSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string(),
  attributeReference: z.string().nullable().optional(),
  passCriteria: z.string(),
  failCriteria: z.string().nullable().optional(),
  naCriteria: z.string().nullable().optional(),
  procedureGuidance: z.string().nullable().optional(),
  tolerances: z.string().nullable().optional(),
  expectedEvidences: z.string().nullable().optional(),
  draftedTest: z.string().nullable().optional(),
  suggestedCategoryId: z.number().nullable().optional(),
  suggestedCategoryName: z.string().nullable().optional(),
  draftedTestDatasets: z.array(draftedTestDatasetSchema).default([]),
});
export type WorkStepDraftAnalyticsColumn = z.infer<
  typeof WorkStepDraftAnalyticsColumnSchema
>;

export const WorkStepProcedureTypeSchema = z.enum(["sample", "direct"]);
export type WorkStepProcedureType = z.infer<typeof WorkStepProcedureTypeSchema>;

// Testing table in the draft plan
export const WorkStepDraftTestingTableSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string(),
  purpose: z.string(),
  procedureType: WorkStepProcedureTypeSchema,
  attributeReferences: z.array(z.string()),
  supportColumns: z.array(WorkStepDraftSupportColumnSchema).default([]),
  attributeTestColumns: z
    .array(WorkStepDraftAttributeTestColumnSchema)
    .default([]),
  analyticsColumns: z.array(WorkStepDraftAnalyticsColumnSchema).default([]),
  removedAnalyticsColumns: z
    .array(WorkStepDraftAnalyticsColumnSchema)
    .default([]),
  testingContext: z.string().nullable().optional(),
  documentationStandards: z.string().nullable().optional(),
});
export type WorkStepDraftTestingTable = z.infer<
  typeof WorkStepDraftTestingTableSchema
>;

// Draft plan structure — no control_type/key_reports/workpaper_examples.
export const WorkStepDraftPlanSchema = z.object({
  workStepName: z.string(),
  workStepDescription: z.string(),
  proposedAttributes: z.array(WorkStepProposedAttributeSchema).nullable(),
  testingTables: z.array(WorkStepDraftTestingTableSchema),
  reasoning: z.string(),
  // The server sends these three beside the plan, never in it, because the
  // plan it dumps is the model it stores; parseWorkStepPlan puts them here.
  // They never go back: patches carry tables, not the plan.
  untestedAttributeReferences: z.array(z.string()).default([]),
  // The session state the server's direct-table rule judges an edit by; sent
  // only when set, and never where hybrid testing is off.
  procedureBasesSeeded: z.boolean().default(false),
  seededDirectTableIds: z.array(z.string()).default([]),
});
export type WorkStepDraftPlan = z.infer<typeof WorkStepDraftPlanSchema>;

/** The fields the server sends beside a plan, which parseWorkStepPlan holds on it. */
export const BesidePlanSchema = WorkStepDraftPlanSchema.pick({
  untestedAttributeReferences: true,
  procedureBasesSeeded: true,
  seededDirectTableIds: true,
});

/** A plan off the wire, with the fields sent beside it read from `besidePlan`, the body that carries both. */
export const parseWorkStepPlan = (
  plan: unknown,
  besidePlan: unknown,
): WorkStepDraftPlan => ({
  ...WorkStepDraftPlanSchema.parse(plan),
  ...BesidePlanSchema.parse(besidePlan),
});

// Testing table input for patches (same as WorkStepDraftTestingTable)
export const WorkStepDraftTestingTableInputSchema =
  WorkStepDraftTestingTableSchema;
export type WorkStepDraftTestingTableInput = z.infer<
  typeof WorkStepDraftTestingTableInputSchema
>;

// Plan patch for structured editing — no key-report/control-type fields.
export const WorkStepPlanPatchSchema = z.object({
  workStepName: z.string().optional(),
  workStepDescription: z.string().optional(),
  addAttributes: z.array(WorkStepProposedAttributeSchema).optional(),
  // Matched id-first (with a reference fallback), so an edit stays an edit even
  // when the attribute code is re-lettered — mirrors apply_patch in the
  // backend fork of collab_control_generator.py.
  updateAttributes: z.array(WorkStepProposedAttributeSchema).optional(),
  removeAttributeRefs: z.array(z.string()).optional(),
  addTestingTables: z.array(WorkStepDraftTestingTableInputSchema).optional(),
  removeTestingTableNames: z.array(z.string()).optional(),
  updateTestingTables: z.array(WorkStepDraftTestingTableInputSchema).optional(),
});
export type WorkStepPlanPatch = z.infer<typeof WorkStepPlanPatchSchema>;

// The backend's PlanPatchRequest is snake_case all the way down (request
// models never use camelCase aliases) — serialize a patch before POSTing.
const supportColumnToRequest = (column: WorkStepDraftSupportColumn) => ({
  id: column.id ?? null,
  name: column.name,
  description: column.description,
  data_type: column.dataType,
});

const attributeTestColumnToRequest = (
  column: WorkStepDraftAttributeTestColumn,
) => ({
  id: column.id ?? null,
  name: column.name,
  pass_criteria: column.passCriteria,
  fail_criteria: column.failCriteria ?? null,
  na_criteria: column.naCriteria ?? null,
  procedure_guidance: column.procedureGuidance ?? null,
  tolerances: column.tolerances ?? null,
  expected_evidences: column.expectedEvidences ?? null,
  attribute_reference: column.attributeReference ?? null,
});

const analyticsColumnToRequest = (column: WorkStepDraftAnalyticsColumn) => ({
  id: column.id ?? null,
  name: column.name,
  attribute_reference: column.attributeReference ?? null,
  pass_criteria: column.passCriteria,
  fail_criteria: column.failCriteria ?? null,
  na_criteria: column.naCriteria ?? null,
  procedure_guidance: column.procedureGuidance ?? null,
  tolerances: column.tolerances ?? null,
  expected_evidences: column.expectedEvidences ?? null,
  drafted_test: column.draftedTest ?? null,
  suggested_category_id: column.suggestedCategoryId ?? null,
  suggested_category_name: column.suggestedCategoryName ?? null,
});

const testingTableToRequest = (table: WorkStepDraftTestingTable) => ({
  id: table.id ?? null,
  name: table.name,
  purpose: table.purpose,
  procedure_type: table.procedureType,
  attribute_references: table.attributeReferences,
  support_columns: table.supportColumns.map(supportColumnToRequest),
  attribute_test_columns: table.attributeTestColumns.map(
    attributeTestColumnToRequest,
  ),
  // Sent back whole on every table edit: the patch replaces the table, so an
  // omitted list would read as every analytics column discarded.
  analytics_columns: table.analyticsColumns.map(analyticsColumnToRequest),
  // A saved column leaves the table only through this list; one merely
  // missing from analytics_columns is refused.
  removed_analytics_columns: table.removedAnalyticsColumns.map(
    analyticsColumnToRequest,
  ),
  testing_context: table.testingContext ?? null,
  documentation_standards: table.documentationStandards ?? null,
});

export const planPatchToRequestBody = (patch: WorkStepPlanPatch) => ({
  work_step_name: patch.workStepName ?? null,
  work_step_description: patch.workStepDescription ?? null,
  add_attributes: patch.addAttributes ?? null,
  update_attributes: patch.updateAttributes ?? null,
  remove_attribute_refs: patch.removeAttributeRefs ?? null,
  add_testing_tables:
    patch.addTestingTables?.map(testingTableToRequest) ?? null,
  update_testing_tables:
    patch.updateTestingTables?.map(testingTableToRequest) ?? null,
  remove_testing_table_names: patch.removeTestingTableNames ?? null,
});

// Message role
// The conversation vocabulary is identical for both entities — see
// models/audit/collabMessage. Re-exported here so this file stays the flow's
// single import surface.
export {
  ClarificationRequestSchema,
  CollabMessageKindSchema,
  CollabMessageRoleSchema,
  CollabMessageSchema,
  UnappliedRequestSchema,
} from "./collabMessage";
export type {
  ClarificationRequest,
  CollabMessage,
  CollabMessageKind,
  CollabMessageRole,
  UnappliedRequest,
} from "./collabMessage";

// Session detail (from GET /work-steps/collab/sessions/{id}/detail)
export const CollabWorkStepSessionDetailSchema = z
  .object({
    id: z.string().uuid(),
    phase: CollabPhaseSchema,
    currentPlan: WorkStepDraftPlanSchema.nullable(),
    // Sent beside the plan; held on it, as parseWorkStepPlan does.
    ...BesidePlanSchema.shape,
    messages: z.array(CollabMessageSchema),
    finalWorkStepId: z.string().uuid().nullable(),
    // The work step a revise session was seeded from; the backend's authoritative
    // revise-vs-generation discriminator (set => revise). Null on generation sessions.
    sourceWorkStepId: z.string().uuid().nullable().optional(),
    errorMessage: z.string().nullable(),
    canUndo: z.boolean().optional(),
    undoCount: z.number().optional(),
    externalRef: z.string().nullable().optional(),
    // Plan fields the session's Optro link locks (e.g. "work_step_name"); the
    // backend refuses edits to them, so the plan editor shows them read-only.
    optroLockedFields: z.array(z.string()).optional(),
    // True while the revise session's seeded analytics columns still await
    // their drafted tests; the page streams draft-pending-columns to fill them.
    seededColumnsToDraft: z.boolean().default(false),
  })
  .transform(
    ({
      untestedAttributeReferences,
      procedureBasesSeeded,
      seededDirectTableIds,
      ...detail
    }) => ({
      ...detail,
      currentPlan: detail.currentPlan && {
        ...detail.currentPlan,
        untestedAttributeReferences,
        procedureBasesSeeded,
        seededDirectTableIds,
      },
    }),
  );
export type CollabWorkStepSessionDetail = z.infer<
  typeof CollabWorkStepSessionDetailSchema
>;

// Create session response
export const CreateCollabWorkStepSessionResponseSchema = z.object({
  sessionId: z.string().uuid(),
  externalRef: z.string().nullable().optional(),
});
export type CreateCollabWorkStepSessionResponse = z.infer<
  typeof CreateCollabWorkStepSessionResponseSchema
>;

// Document upload response
export const CollabWorkStepUploadedDocumentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileType: z.string(),
  state: z.enum(["pending", "processing", "ready", "failed"]),
});
export type CollabWorkStepUploadedDocument = z.infer<
  typeof CollabWorkStepUploadedDocumentSchema
>;

// A document attached to a work-step generation session, as returned by
// GET/POST /work-steps/collab/sessions/{id}/documents. `work_step_document_id` is
// the junction row id — the one the delete endpoint keys on, distinct from the
// underlying document_id that may be shared with other work steps/sessions.
export const WorkStepDocumentInfoSchema = z.object({
  workStepDocumentId: z.string(),
  documentId: z.string(),
  fileName: z.string(),
  fileType: z.string(),
  // Broader than the collab upload's four states (a persisted doc can be "failed" etc.);
  // kept as a plain string so the list never fails to parse on an unexpected state.
  state: z.string(),
});
export type WorkStepDocumentInfo = z.infer<typeof WorkStepDocumentInfoSchema>;

// The persisted work step's own documents (`/work-steps/work-step/{id}/documents`)
// share the collab session endpoints' camelCase wire shape, so both document
// sources parse with the same schema and are interchangeable in the panel.
export const PersistedWorkStepDocumentInfoSchema = WorkStepDocumentInfoSchema;

export const CollabWorkStepDocumentUploadResponseSchema = z.object({
  documents: z.array(CollabWorkStepUploadedDocumentSchema),
  message: z.string(),
});
export type CollabWorkStepDocumentUploadResponse = z.infer<
  typeof CollabWorkStepDocumentUploadResponseSchema
>;

// Phase display information for UI
export const PHASE_DISPLAY = collabPhaseDisplay({
  generating: "Creating final work step",
  complete: "Work step created",
});

// A row of the work step that changed since a refine session began.
const WorkStepDriftItemSchema = z.object({
  kind: z.enum(["work_step", "attribute", "testing_table"]),
  id: z.string().nullable(),
  label: z.string(),
});
export type WorkStepDriftItem = z.infer<typeof WorkStepDriftItemSchema>;

// The finalize 409 detail: the work step was edited while the refine session
// sat open, and finalizing ("last finalize wins") would overwrite those edits.
export const WorkStepEditedSinceSessionStartSchema = z.object({
  code: z.literal("work_step_edited_since_session_start"),
  changes: z.object({
    added: z.array(WorkStepDriftItemSchema),
    removed: z.array(WorkStepDriftItemSchema),
    edited: z.array(WorkStepDriftItemSchema),
  }),
  test_runs_at_risk: z.number(),
});
export type WorkStepEditedSinceSessionStart = z.infer<
  typeof WorkStepEditedSinceSessionStartSchema
>;
